import { useSourceLanguage } from "../i18n/LocalizedCopy";
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  bulkUpdateNotifications,
  fetchNotificationPreferences,
  fetchNotifications,
  fetchNotificationUnreadCount,
  patchNotification as patchNotificationRequest,
  updateNotificationPreference,
} from "./notificationsApi";
import {
  DEFAULT_NOTIFICATION_FILTERS,
  mergeNotificationItems,
  normalizeNotificationFilters,
} from "./notificationModel";

export const NOTIFICATION_INVALIDATED_EVENT = "eternua:notifications-invalidated";
const LEGACY_NOTIFICATION_REFRESH_EVENT = "eternua:notifications-refresh-requested";
const NOTIFICATION_CHANNEL_NAME = "eternua-notification-center";
const NOTIFICATION_SIGNAL_STORAGE_KEY = "eternua.notification-center.signal.v1";
const PREVIEW_LIMIT = 5;

export const NotificationCenterContext = createContext(null);

function isOnline() {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

function isVisible() {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

function postCrossTabInvalidation(detail = {}) {
  if (typeof BroadcastChannel === "function") {
    try {
      const channel = new BroadcastChannel(NOTIFICATION_CHANNEL_NAME);
      channel.postMessage({ type: "invalidate", detail });
      channel.close();
      return;
    } catch (_error) {
      // Le signal storage ci-dessous reste disponible si le canal est refusé.
    }
  }
  try {
    window.localStorage.setItem(
      NOTIFICATION_SIGNAL_STORAGE_KEY,
      JSON.stringify({ type: "invalidate", detail, nonce: `${Date.now()}:${Math.random()}` })
    );
    window.localStorage.removeItem(NOTIFICATION_SIGNAL_STORAGE_KEY);
  } catch (_error) {
    // Le signal inter-onglets est une accélération ; le polling serveur reste le repli.
  }
}

export function broadcastNotificationInvalidation(detail = {}) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(NOTIFICATION_INVALIDATED_EVENT, { detail }));
  }
  postCrossTabInvalidation(detail);
}

function initialPreferences() {
  return {
    items: [],
    emailDigestsEnabled: false,
    availableEmailFrequencies: ["immediate", "never"],
    quietHours: { enabled: false, start: "22:00", end: "07:00", timezone: "Europe/Paris" },
  };
}

function sameFilters(left, right) {
  return left.status === right.status
    && left.category === right.category
    && left.universe === right.universe;
}

function isDefaultFilters(filters) {
  return sameFilters(filters, DEFAULT_NOTIFICATION_FILTERS);
}

function replaceNotification(items, incoming) {
  return items.map((item) => (item.id === incoming.id ? incoming : item));
}

function AccountScopedNotificationCenterProvider({
  enabled = false,
  accountKey = null,
  children,
  pollIntervalMs = 45_000,
}) {
  useSourceLanguage();
  const normalizedAccountKey = String(accountKey || "").trim();
  const active = Boolean(enabled && normalizedAccountKey);
  const [items, setItems] = useState([]);
  const [previewItems, setPreviewItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [listStatus, setListStatus] = useState("idle");
  const [listError, setListError] = useState("");
  const [countStatus, setCountStatus] = useState("idle");
  const [countError, setCountError] = useState("");
  const [nextCursor, setNextCursor] = useState(null);
  const [filters, setFiltersState] = useState(DEFAULT_NOTIFICATION_FILTERS);
  const [preferences, setPreferences] = useState(initialPreferences);
  const [preferencesStatus, setPreferencesStatus] = useState("idle");
  const [preferencesError, setPreferencesError] = useState("");

  const epochRef = useRef(0);
  const listRequestRef = useRef({ sequence: 0, controller: null, key: "", promise: null });
  const previewRequestRef = useRef({ sequence: 0, controller: null, key: "", promise: null });
  const countRequestRef = useRef({ sequence: 0, controller: null, key: "", promise: null });
  const preferencesRequestRef = useRef({ sequence: 0, controller: null, key: "", promise: null });
  const loadPreferencesRef = useRef(() => Promise.resolve(null));
  const preferencesLoadedRef = useRef(false);

  const abortRequests = useCallback(() => {
    [listRequestRef, previewRequestRef, countRequestRef, preferencesRequestRef].forEach((requestRef) => {
      requestRef.current.controller?.abort();
      requestRef.current = {
        sequence: requestRef.current.sequence + 1,
        controller: null,
        key: "",
        promise: null,
      };
    });
  }, []);

  useEffect(() => {
    epochRef.current += 1;
    abortRequests();
    setItems([]);
    setPreviewItems([]);
    setUnreadCount(0);
    setListStatus("idle");
    setListError("");
    setCountStatus("idle");
    setCountError("");
    setNextCursor(null);
    setFiltersState(DEFAULT_NOTIFICATION_FILTERS);
    setPreferences(initialPreferences());
    preferencesLoadedRef.current = false;
    setPreferencesStatus("idle");
    setPreferencesError("");
    return abortRequests;
  }, [abortRequests, active, normalizedAccountKey]);

  const setFilters = useCallback((value) => {
    setFiltersState((current) => {
      const requested = typeof value === "function" ? value(current) : value;
      const next = normalizeNotificationFilters(requested);
      if (sameFilters(current, next)) return current;
      setItems([]);
      setNextCursor(null);
      setListError("");
      return next;
    });
  }, []);

  const loadListPage = useCallback(({ cursor = null, append = false } = {}) => {
    if (!active) return Promise.resolve(null);
    const epoch = epochRef.current;
    const requestFilters = normalizeNotificationFilters(filters);
    const key = `${normalizedAccountKey}:${JSON.stringify(requestFilters)}:${cursor || "first"}:${append ? "append" : "replace"}`;
    if (listRequestRef.current.key === key && listRequestRef.current.promise) {
      return listRequestRef.current.promise;
    }

    listRequestRef.current.controller?.abort();
    const controller = new AbortController();
    const sequence = listRequestRef.current.sequence + 1;
    setListStatus("loading");
    setListError("");

    const promise = fetchNotifications(
      { filters: requestFilters, cursor },
      { signal: controller.signal }
    ).then((page) => {
      if (epoch !== epochRef.current || sequence !== listRequestRef.current.sequence) return null;
      setItems((current) => append ? mergeNotificationItems(current, page.items) : page.items);
      setNextCursor(page.nextCursor);
      if (!append && isDefaultFilters(requestFilters)) {
        setPreviewItems(page.items.slice(0, PREVIEW_LIMIT));
      }
      setListStatus("ready");
      return page;
    }).catch((error) => {
      if (error?.name === "AbortError") return null;
      if (epoch === epochRef.current && sequence === listRequestRef.current.sequence) {
        setListStatus("error");
        setListError(error?.message || "Les notifications sont momentanément indisponibles.");
      }
      throw error;
    }).finally(() => {
      if (sequence === listRequestRef.current.sequence) {
        listRequestRef.current = { sequence, controller: null, key: "", promise: null };
      }
    });

    listRequestRef.current = { sequence, controller, key, promise };
    return promise;
  }, [active, filters, normalizedAccountKey]);

  const loadPreview = useCallback(() => {
    if (!active) return Promise.resolve(null);
    if (isDefaultFilters(filters)) return loadListPage();
    const epoch = epochRef.current;
    const key = `${normalizedAccountKey}:preview`;
    if (previewRequestRef.current.key === key && previewRequestRef.current.promise) {
      return previewRequestRef.current.promise;
    }
    previewRequestRef.current.controller?.abort();
    const controller = new AbortController();
    const sequence = previewRequestRef.current.sequence + 1;
    const promise = fetchNotifications(
      { filters: DEFAULT_NOTIFICATION_FILTERS },
      { signal: controller.signal }
    ).then((page) => {
      if (epoch === epochRef.current && sequence === previewRequestRef.current.sequence) {
        setPreviewItems(page.items.slice(0, PREVIEW_LIMIT));
      }
      return page;
    }).catch((error) => {
      if (error?.name === "AbortError") return null;
      throw error;
    }).finally(() => {
      if (sequence === previewRequestRef.current.sequence) {
        previewRequestRef.current = { sequence, controller: null, key: "", promise: null };
      }
    });
    previewRequestRef.current = { sequence, controller, key, promise };
    return promise;
  }, [active, filters, loadListPage, normalizedAccountKey]);

  const loadCount = useCallback(() => {
    if (!active) return Promise.resolve(null);
    const epoch = epochRef.current;
    const key = `${normalizedAccountKey}:count`;
    if (countRequestRef.current.key === key && countRequestRef.current.promise) {
      return countRequestRef.current.promise;
    }
    countRequestRef.current.controller?.abort();
    const controller = new AbortController();
    const sequence = countRequestRef.current.sequence + 1;
    setCountStatus("loading");
    setCountError("");
    const promise = fetchNotificationUnreadCount({ signal: controller.signal })
      .then((count) => {
        if (epoch === epochRef.current && sequence === countRequestRef.current.sequence) {
          setUnreadCount(count);
          setCountStatus("ready");
        }
        return count;
      })
      .catch((error) => {
        if (error?.name === "AbortError") return null;
        if (epoch === epochRef.current && sequence === countRequestRef.current.sequence) {
          setCountStatus("error");
          setCountError(error?.message || "Le compteur est momentanément indisponible.");
        }
        throw error;
      })
      .finally(() => {
        if (sequence === countRequestRef.current.sequence) {
          countRequestRef.current = { sequence, controller: null, key: "", promise: null };
        }
      });
    countRequestRef.current = { sequence, controller, key, promise };
    return promise;
  }, [active, normalizedAccountKey]);

  const refresh = useCallback(async ({ includeList = true } = {}) => {
    if (!active || !isOnline()) return [];
    const operations = [loadCount()];
    if (includeList) operations.push(loadListPage());
    if (!includeList || !isDefaultFilters(filters)) operations.push(loadPreview());
    return Promise.allSettled(operations);
  }, [active, filters, loadCount, loadListPage, loadPreview]);

  const invalidate = useCallback(({ includeList = true } = {}) => {
    if (!active) return Promise.resolve([]);
    postCrossTabInvalidation({ includeList });
    return refresh({ includeList });
  }, [active, refresh]);

  useEffect(() => {
    if (!active) return undefined;
    refresh({ includeList: true });
    return undefined;
  }, [active, filters, normalizedAccountKey, refresh]);

  useEffect(() => {
    if (!active) return undefined;
    const refreshFromSignal = (detail = {}) => {
      if (detail?.preferences) preferencesLoadedRef.current = true;
      if (!isVisible() || !isOnline()) return;
      refresh({ includeList: detail?.includeList !== false });
      if (preferencesLoadedRef.current) {
        loadPreferencesRef.current().catch(() => {
          // Le provider expose l’erreur ; un signal global ne doit pas créer de rejet non géré.
        });
      }
    };
    const handleWindowFocus = () => refreshFromSignal();
    const handleOnline = () => refreshFromSignal();
    const handleVisibility = () => {
      if (isVisible()) refreshFromSignal();
    };
    const handleInvalidated = (event) => refreshFromSignal(event?.detail);
    const handleStorage = (event) => {
      if (event.key !== NOTIFICATION_SIGNAL_STORAGE_KEY || !event.newValue) return;
      try {
        const payload = JSON.parse(event.newValue);
        if (payload?.type === "invalidate") refreshFromSignal(payload.detail);
      } catch (_error) {
        // Signal malformé ignoré : aucune donnée métier n’est lue depuis le stockage.
      }
    };
    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener(NOTIFICATION_INVALIDATED_EVENT, handleInvalidated);
    window.addEventListener(LEGACY_NOTIFICATION_REFRESH_EVENT, handleInvalidated);
    window.addEventListener("storage", handleStorage);

    let channel = null;
    if (typeof BroadcastChannel === "function") {
      try {
        channel = new BroadcastChannel(NOTIFICATION_CHANNEL_NAME);
        channel.addEventListener("message", (event) => {
          if (event?.data?.type === "invalidate") refreshFromSignal(event.data.detail);
        });
      } catch (_error) {
        channel = null;
        // Le listener storage installé ci-dessus reste le repli inter-onglets.
      }
    }
    return () => {
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener(NOTIFICATION_INVALIDATED_EVENT, handleInvalidated);
      window.removeEventListener(LEGACY_NOTIFICATION_REFRESH_EVENT, handleInvalidated);
      window.removeEventListener("storage", handleStorage);
      channel?.close();
    };
  }, [active, refresh]);

  useEffect(() => {
    if (!active) return undefined;
    const interval = Number.isFinite(Number(pollIntervalMs)) && Number(pollIntervalMs) >= 15_000
      ? Number(pollIntervalMs)
      : 45_000;
    const timer = window.setInterval(() => {
      if (isVisible() && isOnline()) refresh({ includeList: true });
    }, interval);
    return () => window.clearInterval(timer);
  }, [active, pollIntervalMs, refresh]);

  const loadMore = useCallback(() => {
    if (!nextCursor || listStatus === "loading") return Promise.resolve(null);
    return loadListPage({ cursor: nextCursor, append: true });
  }, [listStatus, loadListPage, nextCursor]);

  const patchNotification = useCallback(async (publicId, action, extra = {}) => {
    if (!active) throw new Error("Le centre de notifications est désactivé.");
    const epoch = epochRef.current;
    const updated = await patchNotificationRequest(publicId, action, extra);
    if (epoch !== epochRef.current) return updated;
    setItems((current) => replaceNotification(current, updated));
    setPreviewItems((current) => replaceNotification(current, updated));
    await invalidate({ includeList: true });
    return updated;
  }, [active, invalidate]);

  const bulkAction = useCallback(async (action, publicIds = []) => {
    if (!active) throw new Error("Le centre de notifications est désactivé.");
    const epoch = epochRef.current;
    const result = await bulkUpdateNotifications(action, publicIds);
    if (epoch !== epochRef.current) return result;
    if (result.notifications.length) {
      setItems((current) => mergeNotificationItems(current, result.notifications));
      setPreviewItems((current) => mergeNotificationItems(current, result.notifications).slice(0, PREVIEW_LIMIT));
    }
    await invalidate({ includeList: true });
    return result;
  }, [active, invalidate]);

  const markSeen = useCallback((publicIds = []) => {
    const ids = Array.from(new Set(publicIds.map(String))).filter(Boolean);
    if (!ids.length) return Promise.resolve(null);
    return bulkAction("mark_seen", ids);
  }, [bulkAction]);

  const loadPreferences = useCallback(() => {
    preferencesLoadedRef.current = true;
    if (!active) return Promise.resolve(initialPreferences());
    const epoch = epochRef.current;
    const key = `${normalizedAccountKey}:preferences`;
    if (preferencesRequestRef.current.key === key && preferencesRequestRef.current.promise) {
      return preferencesRequestRef.current.promise;
    }
    preferencesRequestRef.current.controller?.abort();
    const controller = new AbortController();
    const sequence = preferencesRequestRef.current.sequence + 1;
    setPreferencesStatus("loading");
    setPreferencesError("");
    const promise = fetchNotificationPreferences({ signal: controller.signal })
      .then((payload) => {
        if (epoch === epochRef.current && sequence === preferencesRequestRef.current.sequence) {
          setPreferences(payload);
          setPreferencesStatus("ready");
        }
        return payload;
      })
      .catch((error) => {
        if (error?.name === "AbortError") return null;
        if (epoch === epochRef.current && sequence === preferencesRequestRef.current.sequence) {
          setPreferencesStatus("error");
          setPreferencesError(error?.message || "Les préférences sont momentanément indisponibles.");
        }
        throw error;
      })
      .finally(() => {
        if (sequence === preferencesRequestRef.current.sequence) {
          preferencesRequestRef.current = { sequence, controller: null, key: "", promise: null };
        }
      });
    preferencesRequestRef.current = { sequence, controller, key, promise };
    return promise;
  }, [active, normalizedAccountKey]);

  useEffect(() => {
    loadPreferencesRef.current = loadPreferences;
  }, [loadPreferences]);

  const updatePreferences = useCallback(async (category, patch) => {
    if (!active) throw new Error("Le centre de notifications est désactivé.");
    const epoch = epochRef.current;
    const updated = await updateNotificationPreference(category, patch);
    if (epoch !== epochRef.current) return updated;
    setPreferences((current) => ({
      ...current,
      items: current.items.some((item) => item.category === updated.category)
        ? current.items.map((item) => item.category === updated.category ? updated : item)
        : [...current.items, updated],
    }));
    setPreferencesStatus("ready");
    setPreferencesError("");
    postCrossTabInvalidation({ includeList: false, preferences: true });
    return updated;
  }, [active]);

  const value = useMemo(() => ({
    enabled: active,
    items,
    previewItems,
    unreadCount,
    listStatus,
    listError,
    countStatus,
    countError,
    nextCursor,
    hasMore: Boolean(nextCursor),
    filters,
    setFilters,
    refresh,
    loadMore,
    patchNotification,
    bulkAction,
    markSeen,
    preferences,
    preferencesStatus,
    preferencesError,
    loadPreferences,
    updatePreferences,
    invalidate,
  }), [
    active,
    bulkAction,
    countError,
    countStatus,
    filters,
    invalidate,
    items,
    listError,
    listStatus,
    loadMore,
    loadPreferences,
    markSeen,
    nextCursor,
    patchNotification,
    preferences,
    preferencesError,
    preferencesStatus,
    previewItems,
    refresh,
    setFilters,
    unreadCount,
    updatePreferences,
  ]);

  return (
    <NotificationCenterContext.Provider value={value}>
      {children}
    </NotificationCenterContext.Provider>
  );
}

export function NotificationCenterProvider(props) {
  useSourceLanguage();
  const accountKey = String(props.accountKey || "").trim();
  return (
    <AccountScopedNotificationCenterProvider
      {...props}
      key={accountKey || "anonymous"}
      accountKey={accountKey}
    />
  );
}

export default NotificationCenterProvider;
